/**
 * DashboardFourPsPage — Tela "Painel" (Funil de marketing).
 * Reconstruida fiel ao prototipo dashboard-m3-expressive.html (F3).
 *
 * ISOLAMENTO:
 *   - CSS proprio: screen-painel.css (importado aqui, nao em global.css)
 *   - Componentes novos com prefixo Painel: PainelFunnelCard, PainelDrilldownModal
 *   - Nao edita global.css, lib/i18n.ts, AppLayout.tsx, outras rotas
 *
 * DADOS:
 *   - Consome /kpi/snapshot?category=funnel (camadas do funil)
 *   - Valores null/N/A exibem "—" + flag honesta (nao inventa numero)
 *   - Aba Vendas preserva KPIs de venda (Investimento movido para Trafego)
 *
 * A11y AA: contrast tokens.css, focus ring, aria-*, keyboard nav,
 *           role=tablist/tab/tabpanel, aria-selected, ESC fecha modal.
 */

import '../styles/screen-painel.css';
import '../styles/screen-ads.css';
import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { t } from '../lib/i18n';
import { SkeletonGrid } from '../components/Skeleton';
import { apiClient, ApiError } from '../lib/api-client';
import { PainelFunnelCard } from '../components/PainelFunnelCard';
import type { PainelSourceKey } from '../components/PainelFunnelCard';
import { PainelDrilldownModal } from '../components/PainelDrilldownModal';
import type { PainelDrilldownItem } from '../components/PainelDrilldownModal';
import { useFilterContext } from '../lib/filter-context';

// ---------------------------------------------------------------------------
// Tipos compartilhados
// ---------------------------------------------------------------------------

interface KpiItem {
  readonly id: string;
  readonly title: string;
  readonly value: string | null;
  readonly unit?: string;
  readonly delta?: number | null;
  readonly series?: readonly number[];
  readonly description?: string;
  /** Percentual de barra horizontal (0-100) — exclusivo do funil. */
  readonly barPct?: number;
  /** Fonte do dado. */
  readonly source?: PainelSourceKey;
  /** Nota de origem para o modal de drilldown. */
  readonly note?: string;
}

// ---------------------------------------------------------------------------
// Funil — tipos
// ---------------------------------------------------------------------------

type FunnelLayer = 'visibilidade' | 'engajamento' | 'trafego' | 'vendas';

type FunnelKpi = KpiItem;

interface FunnelData {
  readonly visibilidade: readonly FunnelKpi[];
  readonly engajamento: readonly FunnelKpi[];
  readonly trafego: readonly FunnelKpi[];
  readonly vendas: readonly FunnelKpi[];
  readonly headline?: string;
  readonly no_sales_data?: boolean;
  /** Metricas hero para o bento (opcional — retornadas pelo backend quando disponiveis). */
  readonly hero?: {
    readonly faturamento?: string | null;
    readonly faturamento_delta?: number | null;
    readonly faturamento_note?: string;
    readonly bolos_vendidos?: string | null;
    readonly bolos_vendidos_delta?: number | null;
    readonly alcance_total?: string | null;
    readonly alcance_total_delta?: number | null;
  };
  /** Frase proativa da CROSS (opcional). */
  readonly proactive_phrase?: string;
  /** Timestamp da ultima coleta (ex: "hoje, 06:32"). */
  readonly last_collected_at?: string;
}

// ---------------------------------------------------------------------------
// Painel (visao legada — duas camadas + ponte CROSS)
// ---------------------------------------------------------------------------

type PanelTab = 'comercial' | 'digital';

interface PanelComercial {
  readonly resultado: readonly KpiItem[];
  readonly preco: readonly KpiItem[];
  readonly praca: readonly KpiItem[];
  readonly produto: readonly KpiItem[];
}

interface PanelDigital {
  readonly midia_paga: readonly KpiItem[];
  readonly organico: readonly KpiItem[];
}

interface PanelData {
  readonly bridge: readonly KpiItem[];
  readonly comercial: PanelComercial;
  readonly digital: PanelDigital;
  readonly gaps: { readonly produto: true };
  readonly headline?: string;
  readonly no_sales_data?: boolean;
}

// ---------------------------------------------------------------------------
// Periodo e filtros
// ---------------------------------------------------------------------------

type Period = '7d' | '30d' | '90d' | '365d';
/** Alias local para compatibilidade com FilterContext.PeriodMode */
type PeriodMode = Period | 'month';
/** Alias local para compatibilidade com FilterContext.SourceFilter */
type SourceFilter = 'all' | 'instagram' | 'meta_ads' | 'google_drive';

const ROLLING_PERIODS: readonly Period[] = ['7d', '30d', '90d', '365d'];

const CURRENT_YEAR = new Date().getFullYear();
const AVAILABLE_YEARS: readonly number[] = Array.from(
  { length: Math.max(1, CURRENT_YEAR - 2024) },
  (_, i) => 2025 + i,
);

// ---------------------------------------------------------------------------
// Load state
// ---------------------------------------------------------------------------

type LoadState<D> =
  | { kind: 'loading' }
  | { kind: 'empty' }
  | { kind: 'ready'; data: D }
  | { kind: 'error' };

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function resolveKpiValue(kpi: KpiItem): string {
  if (kpi.value === null || kpi.value === undefined || kpi.value === 'N/A') {
    return '—';
  }
  if (kpi.unit !== undefined && kpi.unit.length > 0) {
    return `${kpi.value} ${kpi.unit}`;
  }
  return kpi.value;
}

function isColdStartFunnel(data: FunnelData): boolean {
  return (
    data.visibilidade.length === 0 &&
    data.engajamento.length === 0 &&
    data.trafego.length === 0 &&
    data.vendas.length === 0
  );
}

function isColdStartPanel(data: PanelData): boolean {
  return (
    data.bridge.length === 0 &&
    data.comercial.resultado.length === 0 &&
    data.comercial.preco.length === 0 &&
    data.comercial.praca.length === 0 &&
    data.digital.midia_paga.length === 0 &&
    data.digital.organico.length === 0
  );
}

function buildFunnelUrl(
  mode: PeriodMode,
  year: number,
  month: number,
  source: SourceFilter,
): string {
  const base = `/kpi/snapshot?category=funnel&source=${source}`;
  if (mode === 'month') return `${base}&year=${year}&month=${month}`;
  return `${base}&period=${mode}`;
}

function buildPanelUrl(mode: PeriodMode, year: number, month: number): string {
  const base = `/kpi/snapshot?category=panel`;
  if (mode === 'month') return `${base}&year=${year}&month=${month}`;
  return `${base}&period=${mode}`;
}

/** Label amigavel do periodo ativo. */
function periodDisplayLabel(mode: PeriodMode, month: number, year: number): string {
  if (mode === 'month') {
    const monthName = t(`fourPs.monthFilter.months.m${month}` as 'fourPs.monthFilter.months.m1');
    return `${monthName} ${year}`;
  }
  return t(`fourPs.period.d${mode.replace('d', '')}` as 'fourPs.period.d7');
}

/** Inferencia do source para o mapa de fontes. */
function inferSourceKey(kpi: KpiItem): PainelSourceKey {
  if (kpi.source !== undefined) return kpi.source;
  const title = kpi.title.toLowerCase();
  if (
    title.includes('instagram') ||
    title.includes('alcance') ||
    title.includes('seguidor') ||
    title.includes('curtida') ||
    title.includes('comentario') ||
    title.includes('salvamento') ||
    title.includes('post') ||
    title.includes('engajamento') ||
    title.includes('perfil')
  )
    return 'ig';
  if (
    title.includes('anuncio') ||
    title.includes('cpc') ||
    title.includes('ctr') ||
    title.includes('cpm') ||
    title.includes('meta') ||
    title.includes('impressoe') ||
    title.includes('clique') ||
    title.includes('frequencia') ||
    title.includes('investimento')
  )
    return 'ads';
  if (
    title.includes('faturamento') ||
    title.includes('ticket') ||
    title.includes('bolo') ||
    title.includes('venda') ||
    title.includes('receita') ||
    title.includes('retorno') ||
    title.includes('drive') ||
    title.includes('planilha')
  )
    return 'drive';
  return 'other';
}

const SOURCE_LABELS: Record<PainelSourceKey, string> = {
  ig: t('panel.sourceIg'),
  ads: t('panel.sourceAds'),
  drive: t('panel.sourceDrive'),
  other: t('panel.sourceOther'),
};

function sourceLabel(key: PainelSourceKey): string {
  return SOURCE_LABELS[key];
}

// ---------------------------------------------------------------------------
// Strings do modal e hero — agora via i18n (migradas de hardcoded para t()).
// ---------------------------------------------------------------------------

const MODAL_STRINGS = {
  sourceLabel: t('panel.modalSourceLabel'),
  accountLabel: t('panel.modalAccountLabel'),
  periodLabel: t('panel.modalPeriodLabel'),
  collectedLabel: t('panel.modalCollectedLabel'),
  closeBtnLabel: t('fourPs.closeDrilldown'),
  originLabel: t('panel.modalOriginLabel'),
  ctaLabel: t('panel.modalCtaLabel'),
  noDataLabel: '—',
} as const;

// ---------------------------------------------------------------------------
// Strings editoriais do page header — agora via i18n.
// ---------------------------------------------------------------------------
const PAGE_HEADER_STRINGS = {
  eyebrow: t('panel.pageEyebrow'),
  titlePre: t('panel.pageTitlePre'),
  titleEm: t('panel.pageTitleEm'),
  sub: t('panel.pageSub'),
  metaLoading: t('panel.pageMetaLoading'),
  funnelHeadline: t('panel.pageFunnelHeadline'),
  metaPeriodLabel: t('panel.pageMetaPeriodLabel'),
  metaSources: t('panel.pageMetaSources'),
} as const;

/** Strings de UI do hero bento — agora via i18n. */
const HERO_STRINGS = {
  faturamento: {
    tag: t('panel.heroFaturamentoTag'),
    label: t('panel.heroFaturamentoLabel'),
    noData: t('panel.heroFaturamentoNoData'),
    src: t('panel.heroFaturamentoSrc'),
    verOrigem: t('panel.heroFaturamentoVerOrigem'),
    semComp: t('panel.heroFaturamentoSemComp'),
  },
  bolos: {
    label: t('panel.heroProdutoLabel'),
    sub: t('panel.heroProdutoSub'),
    noData: t('panel.heroProdutoNoData'),
  },
  alcance: {
    label: t('panel.heroAlcanceLabel'),
    sub: t('panel.heroAlcanceSub'),
    noData: t('panel.heroAlcanceNoData'),
  },
  insight: {
    eyebrow: t('panel.heroInsightEyebrow'),
    fallback: t('panel.heroInsightFallback'),
    fallbackBold: t('panel.heroInsightFallbackBold'),
    fallbackSuffix: t('panel.heroInsightFallbackSuffix'),
    cta: t('panel.heroInsightCta'),
  },
  noData: t('panel.heroNoData'),
  linkLabel: t('panel.heroLinkLabel'),
  section: t('panel.heroSection'),
  chatChip: t('panel.heroChatChip'),
  vsAnterior: t('panel.heroVsAnterior'),
} as const;

// ---------------------------------------------------------------------------
// Helper: verifica se um KpiItem nao tem dado real no periodo
// ---------------------------------------------------------------------------

function kpiHasNoData(kpi: KpiItem): boolean {
  return kpi.value === null || kpi.value === undefined || kpi.value === 'N/A';
}

// ---------------------------------------------------------------------------
// Componente: Hero Bento (faturamento + insight + bolos + alcance)
// FLAG: card-revenue, card-bolos, card-alcance dependem dos dados do backend.
//       O campo hero{} e opcional na resposta atual — exibe vazio honesto
//       quando ausente (nao inventa numero). Backend gap marcado abaixo.
// ---------------------------------------------------------------------------

interface PainelHeroBentoProps {
  readonly hero: FunnelData['hero'];
  /** Insight/headline do periodo (opcional). */
  readonly headline?: string | undefined;
  readonly periodLabel: string;
  /** Timestamp da ultima coleta. */
  readonly lastCollectedAt?: string | undefined;
  readonly onRevenueClick: () => void;
  readonly onBolosClick: () => void;
  readonly onAlcanceClick: () => void;
  readonly onChatClick: () => void;
}

function PainelHeroBento({
  hero,
  headline,
  periodLabel,
  onRevenueClick,
  onBolosClick,
  onAlcanceClick,
  onChatClick,
}: PainelHeroBentoProps): JSX.Element {
  // GAP BACKEND: hero{} nao existe ainda no endpoint /kpi/snapshot?category=funnel.
  // Quando o backend implementar, mapear: hero.faturamento, hero.bolos_vendidos, hero.alcance_total.
  // Por ora, exibe "—" (degradacao honesta).
  const fat = hero?.faturamento ?? null;
  const fatDelta = hero?.faturamento_delta ?? null;
  const bolos = hero?.bolos_vendidos ?? null;
  const bolosDelta = hero?.bolos_vendidos_delta ?? null;
  const alcance = hero?.alcance_total ?? null;
  const alcanceDelta = hero?.alcance_total_delta ?? null;

  const hasFat = fat !== null;
  const hasBolos = bolos !== null;
  const hasAlcance = alcance !== null;

  return (
    <div className="painel-hero-bento" aria-label="Resumo do periodo">
      {/* Card Faturamento (heroi roxo) */}
      <button
        type="button"
        className="painel-card-revenue"
        onClick={onRevenueClick}
        aria-label={`${HERO_STRINGS.bolos.label.replace('Bolos vendidos', 'Faturamento')}: ${hasFat ? (fat as string) : HERO_STRINGS.faturamento.noData}`}
      >
        <div className="painel-revenue-circle" aria-hidden="true" />
        <div className="painel-revenue-inner">
          <div>
            <div className="painel-revenue-tag">{periodLabel}</div>
            <span className="painel-revenue-value">{hasFat ? (fat as string) : '—'}</span>
            {hasFat ? (
              <div className="painel-revenue-label">{HERO_STRINGS.faturamento.label}</div>
            ) : (
              <div className="painel-revenue-label painel-accent-empty">
                {HERO_STRINGS.faturamento.noData}
              </div>
            )}
          </div>
          <div className="painel-revenue-bottom">
            {fatDelta !== null ? (
              <div
                className={`painel-revenue-delta${fatDelta < 0 ? ' painel-delta-down' : ''}`}
                aria-label={fatDelta > 0 ? `subiu ${fatDelta}%` : `caiu ${Math.abs(fatDelta)}%`}
              >
                <span
                  className="material-symbols-rounded"
                  style={{
                    fontSize: 14,
                    verticalAlign: -2,
                    fontVariationSettings: "'FILL' 0,'wght' 600,'GRAD' 0,'opsz' 20",
                  }}
                  aria-hidden="true"
                >
                  {fatDelta >= 0 ? 'trending_up' : 'trending_down'}
                </span>
                {Math.abs(fatDelta)}% {HERO_STRINGS.vsAnterior}
              </div>
            ) : (
              <div className="painel-flag-no-data">{HERO_STRINGS.faturamento.semComp}</div>
            )}
            <div className="painel-revenue-src">
              {HERO_STRINGS.faturamento.src}
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  onRevenueClick();
                }}
              >
                {HERO_STRINGS.faturamento.verOrigem}
              </button>
            </div>
          </div>
        </div>
      </button>

      {/* Card Insight editorial */}
      <button
        type="button"
        className="painel-card-insight"
        onClick={onChatClick}
        aria-label="Resumo do periodo — clique para conversar com o CROSS Flux"
      >
        <div>
          <div className="painel-insight-eyebrow">{HERO_STRINGS.insight.eyebrow}</div>
          <div className="painel-insight-text">
            {headline !== undefined && headline.length > 0 ? (
              headline
            ) : (
              <>
                {HERO_STRINGS.insight.fallback} <strong>{HERO_STRINGS.insight.fallbackBold}</strong>{' '}
                {HERO_STRINGS.insight.fallbackSuffix}
              </>
            )}
          </div>
        </div>
        <div className="painel-insight-src">{HERO_STRINGS.insight.cta}</div>
      </button>

      {/* Card Bolos vendidos */}
      <button
        type="button"
        className="painel-card-accent"
        onClick={onBolosClick}
        aria-label={`Bolos vendidos: ${hasBolos ? (bolos as string) : 'dado indisponivel'}`}
      >
        <div className="painel-accent-label">{HERO_STRINGS.bolos.label}</div>
        <div className="painel-accent-value">{hasBolos ? (bolos as string) : '—'}</div>
        {hasBolos ? (
          <div className="painel-accent-sub">{HERO_STRINGS.bolos.sub}</div>
        ) : (
          <div className="painel-accent-empty">{HERO_STRINGS.bolos.noData}</div>
        )}
        {bolosDelta !== null ? (
          <div className={`painel-accent-delta${bolosDelta < 0 ? ' painel-delta-down' : ''}`}>
            <span
              className="material-symbols-rounded"
              style={{ fontSize: 13, verticalAlign: -2 }}
              aria-hidden="true"
            >
              {bolosDelta >= 0 ? 'trending_up' : 'trending_down'}
            </span>
            {bolosDelta}%
          </div>
        ) : null}
      </button>

      {/* Card Alcance total */}
      <button
        type="button"
        className="painel-card-accent"
        onClick={onAlcanceClick}
        aria-label={`Alcance total: ${hasAlcance ? (alcance as string) : 'dado indisponivel'}`}
      >
        <div className="painel-accent-label">{HERO_STRINGS.alcance.label}</div>
        <div className="painel-accent-value">{hasAlcance ? (alcance as string) : '—'}</div>
        {hasAlcance ? (
          <div className="painel-accent-sub">{HERO_STRINGS.alcance.sub}</div>
        ) : (
          <div className="painel-accent-empty">{HERO_STRINGS.alcance.noData}</div>
        )}
        {alcanceDelta !== null ? (
          <div className={`painel-accent-delta${alcanceDelta < 0 ? ' painel-delta-down' : ''}`}>
            <span
              className="material-symbols-rounded"
              style={{ fontSize: 13, verticalAlign: -2 }}
              aria-hidden="true"
            >
              {alcanceDelta >= 0 ? 'trending_up' : 'trending_down'}
            </span>
            {alcanceDelta}%
          </div>
        ) : null}
      </button>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Componente: Segmented Button M3 (abas do funil)
// ---------------------------------------------------------------------------

interface PainelSegmentedBtnProps {
  readonly activeLayer: FunnelLayer;
  readonly onChange: (layer: FunnelLayer) => void;
  /** Flag de alerta por camada (ex: trafego tem CPC subindo). */
  readonly alerts?: Partial<Record<FunnelLayer, boolean>>;
}

const LAYER_LABELS: Record<FunnelLayer, string> = {
  visibilidade: t('fourPs.tabs.visibilidade'),
  engajamento: t('fourPs.tabs.engajamento'),
  trafego: t('fourPs.tabs.trafego'),
  vendas: t('fourPs.tabs.vendas'),
};

const FUNNEL_LAYERS: readonly FunnelLayer[] = ['visibilidade', 'engajamento', 'trafego', 'vendas'];

function PainelSegmentedBtn({
  activeLayer,
  onChange,
  alerts = {},
}: PainelSegmentedBtnProps): JSX.Element {
  return (
    <nav className="painel-segmented-btn" aria-label="Etapa de resultado" role="tablist">
      {FUNNEL_LAYERS.map((layer) => {
        const isActive = layer === activeLayer;
        return (
          <button
            key={layer}
            type="button"
            role="tab"
            id={`painel-tab-${layer}`}
            aria-selected={isActive}
            aria-controls={`painel-tabpanel-${layer}`}
            className={`painel-seg-btn${isActive ? ' active' : ''}`}
            onClick={() => onChange(layer)}
          >
            <span
              className="painel-seg-check material-symbols-rounded"
              aria-hidden="true"
              style={{ fontVariationSettings: "'FILL' 0,'wght' 600,'GRAD' 0,'opsz' 20" }}
            >
              check
            </span>
            {LAYER_LABELS[layer]}
            {alerts[layer] === true ? (
              <span className="painel-seg-badge" aria-label="alerta">
                !
              </span>
            ) : null}
          </button>
        );
      })}
    </nav>
  );
}

// ---------------------------------------------------------------------------
// Componente: Grade de KPI cards (funil)
// ---------------------------------------------------------------------------

interface PainelKpiGridProps {
  readonly kpis: readonly FunnelKpi[];
  readonly onCardClick: (item: PainelDrilldownItem) => void;
}

// Padrao premium: grade UNIFORME. Todos os cards do funil tem o mesmo tamanho
// (span-4 = 3 por linha) e o numero usa o tratamento padrao (sem cor por
// posicao). A variacao anterior (span 3/4/6 + cor hero/warn por indice) criava
// cards de larguras diferentes — incluindo um card largo quase vazio — e cores
// que nao mapeavam significado, so posicao na grade. Uniformidade = leitura
// consistente e sem espaco exagerado.
const FUNNEL_CARD_SPAN = 4 as const;

function PainelKpiGrid({ kpis, onCardClick }: PainelKpiGridProps): JSX.Element {
  // Dedup por secao: se TODOS os KPIs da camada ativa nao tiverem dado no
  // periodo, exibir a mensagem padrao uma unica vez no lugar da grade.
  const allNoData = kpis.length > 0 && kpis.every(kpiHasNoData);

  if (allNoData) {
    return (
      <p className="painel-section-no-data" role="note">
        {t('digital.noDataForPeriodSection')}
      </p>
    );
  }

  return (
    <div className="painel-kpi-grid" role="list">
      {kpis.map((kpi, idx) => {
        const sk = inferSourceKey(kpi);
        const sl = sourceLabel(sk);
        const displayValue = resolveKpiValue(kpi);
        const span = FUNNEL_CARD_SPAN;
        const variant = 'default' as const;

        const drilldownItem: PainelDrilldownItem = {
          id: kpi.id,
          label: kpi.title,
          value: displayValue,
          source: sk,
          sourceLabel: sl,
          note: kpi.note,
        };

        return (
          <PainelFunnelCard
            key={kpi.id}
            id={kpi.id}
            label={kpi.title}
            value={displayValue === '—' ? null : displayValue}
            delta={kpi.delta ?? null}
            series={kpi.series}
            barPct={kpi.barPct}
            description={kpi.description}
            source={sk}
            sourceLabel={sl}
            linkLabel={HERO_STRINGS.linkLabel}
            noDataLabel={t('digital.noDataForPeriod')}
            span={span}
            variant={variant}
            animDelay={Math.min(idx, 8) * 50}
            onClick={() => onCardClick(drilldownItem)}
          />
        );
      })}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Subcomponentes legados (PanelView) — preservados sem regressao
// ---------------------------------------------------------------------------

function BridgeStrip({
  items,
  onDrilldown,
}: {
  readonly items: readonly KpiItem[];
  readonly onDrilldown: (kpi: KpiItem) => void;
}): JSX.Element | null {
  if (items.length === 0) return null;

  // Dedup por secao: se TODOS os KPIs da ponte nao tiverem dado, exibir mensagem uma unica vez.
  const allNoData = items.every(kpiHasNoData);

  return (
    <section className="bridge-strip" aria-label={t('panel.bridgeLabel')}>
      {allNoData ? (
        <p className="painel-section-no-data" role="note">
          {t('digital.noDataForPeriodSection')}
        </p>
      ) : (
        <div className="kpi-grid">
          {items.map((kpi) => {
            const noData = kpiHasNoData(kpi);
            return (
              <button
                key={kpi.id}
                type="button"
                className="kpi-card kpi-card-clickable"
                aria-label={noData ? `${kpi.title}: ${t('digital.noDataForPeriod')}` : kpi.title}
                onClick={() => onDrilldown(kpi)}
              >
                <h3>{kpi.title}</h3>
                {noData ? (
                  <p className="painel-flag-no-data" role="note">
                    {t('digital.noDataForPeriod')}
                  </p>
                ) : (
                  <p className="kpi-value">{resolveKpiValue(kpi)}</p>
                )}
                {kpi.description !== undefined && kpi.description.length > 0 ? (
                  <p className="kpi-description muted">{kpi.description}</p>
                ) : null}
              </button>
            );
          })}
        </div>
      )}
    </section>
  );
}

function PanelSection({
  title,
  items,
  onDrilldown,
}: {
  readonly title: string;
  readonly items: readonly KpiItem[];
  readonly onDrilldown: (kpi: KpiItem) => void;
}): JSX.Element {
  // Dedup por secao: se TODOS os KPIs da secao nao tiverem dado, exibir mensagem uma unica vez.
  const allNoData = items.length > 0 && items.every(kpiHasNoData);

  return (
    <section className="panel-section">
      <h3 className="panel-section-title">{title}</h3>
      {allNoData ? (
        <p className="painel-section-no-data" role="note">
          {t('digital.noDataForPeriodSection')}
        </p>
      ) : items.length > 0 ? (
        <div className="kpi-grid">
          {items.map((kpi) => {
            const noData = kpiHasNoData(kpi);
            return (
              <button
                key={kpi.id}
                type="button"
                className="kpi-card kpi-card-clickable"
                aria-label={noData ? `${kpi.title}: ${t('digital.noDataForPeriod')}` : kpi.title}
                onClick={() => onDrilldown(kpi)}
              >
                <h3>{kpi.title}</h3>
                {noData ? (
                  <p className="painel-flag-no-data" role="note">
                    {t('digital.noDataForPeriod')}
                  </p>
                ) : (
                  <p className="kpi-value">{resolveKpiValue(kpi)}</p>
                )}
                {kpi.description !== undefined && kpi.description.length > 0 ? (
                  <p className="kpi-description muted">{kpi.description}</p>
                ) : null}
              </button>
            );
          })}
        </div>
      ) : null}
    </section>
  );
}

function ProdutoEmBreve(): JSX.Element {
  return (
    <section className="panel-section">
      <h3 className="panel-section-title">{t('panel.secaoProduto')}</h3>
      <div className="empty-state" aria-live="polite">
        <p>
          <strong>{t('panel.produtoEmBreve')}</strong>
        </p>
        <p className="muted">{t('panel.produtoEmBreveDesc')}</p>
      </div>
    </section>
  );
}

interface PeriodFiltersProps {
  readonly periodMode: PeriodMode;
  readonly selectedYear: number;
  readonly selectedMonth: number;
  readonly onChangePeriod: (mode: PeriodMode) => void;
  readonly onChangeYear: (year: number) => void;
  readonly onChangeMonth: (month: number) => void;
  readonly onApply: () => void;
  readonly periodLabelKey: 'fourPs.period.label' | 'panel.period.label';
  readonly applyLabelKey: 'fourPs.monthFilter.apply' | 'panel.monthFilter.apply';
  readonly monthLabelKey: 'fourPs.monthFilter.monthLabel' | 'panel.monthFilter.monthLabel';
  readonly yearLabelKey: 'fourPs.monthFilter.yearLabel' | 'panel.monthFilter.yearLabel';
}

function PeriodFilters({
  periodMode,
  selectedYear,
  selectedMonth,
  onChangePeriod,
  onChangeYear,
  onChangeMonth,
  onApply,
  periodLabelKey,
  applyLabelKey,
  monthLabelKey,
  yearLabelKey,
}: PeriodFiltersProps): JSX.Element {
  return (
    <>
      <label htmlFor="panel-period">{t(periodLabelKey)}</label>
      <select
        id="panel-period"
        value={periodMode}
        onChange={(e) => onChangePeriod(e.target.value as PeriodMode)}
      >
        {ROLLING_PERIODS.map((p) => (
          <option key={p} value={p}>
            {t(`fourPs.period.d${p.replace('d', '')}` as 'fourPs.period.d7')}
          </option>
        ))}
        <option value="month">{t('fourPs.period.specificMonth')}</option>
      </select>

      {periodMode === 'month' ? (
        <>
          <label htmlFor="panel-month">{t(monthLabelKey)}</label>
          <select
            id="panel-month"
            value={selectedMonth}
            onChange={(e) => onChangeMonth(Number(e.target.value))}
          >
            {Array.from({ length: 12 }, (_, i) => i + 1).map((m) => (
              <option key={m} value={m}>
                {t(`fourPs.monthFilter.months.m${m}` as 'fourPs.monthFilter.months.m1')}
              </option>
            ))}
          </select>

          <label htmlFor="panel-year">{t(yearLabelKey)}</label>
          <select
            id="panel-year"
            value={selectedYear}
            onChange={(e) => onChangeYear(Number(e.target.value))}
          >
            {AVAILABLE_YEARS.map((y) => (
              <option key={y} value={y}>
                {y}
              </option>
            ))}
          </select>

          <button type="button" className="btn-apply-month" onClick={onApply}>
            {t(applyLabelKey)}
          </button>
        </>
      ) : null}
    </>
  );
}

function DrilldownDialog({
  kpi,
  onClose,
}: {
  readonly kpi: KpiItem;
  readonly onClose: () => void;
}): JSX.Element {
  return (
    <div className="dialog-backdrop" role="presentation" onClick={onClose}>
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="drilldown-title"
        className="dialog"
        onClick={(e) => e.stopPropagation()}
      >
        <h2 id="drilldown-title">{t('panel.drilldownTitle')}</h2>
        <p>
          <strong>{kpi.title}</strong>
        </p>
        {kpi.description !== undefined ? <p>{kpi.description}</p> : null}
        <div className="dialog-actions">
          <button type="button" onClick={onClose}>
            {t('panel.closeDrilldown')}
          </button>
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// PanelView — legado preservado
// ---------------------------------------------------------------------------

function PanelView(): JSX.Element {
  const [periodMode, setPeriodMode] = useState<PeriodMode>('30d');
  const [selectedYear, setSelectedYear] = useState<number>(CURRENT_YEAR);
  const [selectedMonth, setSelectedMonth] = useState<number>(new Date().getMonth() + 1);
  const [appliedYear, setAppliedYear] = useState<number>(CURRENT_YEAR);
  const [appliedMonth, setAppliedMonth] = useState<number>(new Date().getMonth() + 1);
  const [activeTab, setActiveTab] = useState<PanelTab>('comercial');
  const [retryKey, setRetryKey] = useState<number>(0);
  const [state, setState] = useState<LoadState<PanelData>>({ kind: 'loading' });
  const [drilldown, setDrilldown] = useState<KpiItem | null>(null);

  useEffect(() => {
    let cancelled = false;
    setState({ kind: 'loading' });
    const url = buildPanelUrl(periodMode, appliedYear, appliedMonth);
    const load = async (): Promise<void> => {
      try {
        const data = await apiClient.get<PanelData | null>(url);
        if (cancelled) return;
        if (data === null) {
          setState({ kind: 'empty' });
          return;
        }
        if (isColdStartPanel(data)) {
          setState({ kind: 'empty' });
          return;
        }
        setState({ kind: 'ready', data });
      } catch (err) {
        if (cancelled) return;
        if (err instanceof ApiError && err.status === 404) {
          setState({ kind: 'empty' });
          return;
        }
        setState({ kind: 'error' });
      }
    };
    void load();
    return () => {
      cancelled = true;
    };
  }, [periodMode, appliedYear, appliedMonth, retryKey]);

  const handleApply = (): void => {
    setAppliedYear(selectedYear);
    setAppliedMonth(selectedMonth);
  };

  return (
    <section aria-labelledby="panel-title">
      <div className="page-heading">
        <h1 id="panel-title">{t('panel.title')}</h1>
        <p className="muted">{t('panel.subtitle')}</p>
        {state.kind === 'ready' && state.data.headline !== undefined ? (
          <p className="hero-message">{state.data.headline}</p>
        ) : null}
      </div>

      <div className="filters-bar" role="group" aria-label={t('panel.period.label')}>
        <PeriodFilters
          periodMode={periodMode}
          selectedYear={selectedYear}
          selectedMonth={selectedMonth}
          onChangePeriod={setPeriodMode}
          onChangeYear={setSelectedYear}
          onChangeMonth={setSelectedMonth}
          onApply={handleApply}
          periodLabelKey="panel.period.label"
          applyLabelKey="panel.monthFilter.apply"
          monthLabelKey="panel.monthFilter.monthLabel"
          yearLabelKey="panel.monthFilter.yearLabel"
        />
      </div>

      {state.kind === 'loading' ? <SkeletonGrid count={4} /> : null}

      {state.kind === 'error' ? (
        <div role="alert" className="error">
          <p>{t('panel.error')}</p>
          <button
            type="button"
            onClick={() => {
              setRetryKey((k) => k + 1);
            }}
          >
            {t('panel.retryLabel')}
          </button>
        </div>
      ) : null}

      {state.kind === 'empty' ? (
        <section className="empty-state" aria-live="polite">
          <span className="empty-state-icon material-symbols-rounded" aria-hidden="true">
            bar_chart
          </span>
          <h2>{t('panel.coldStartTitle')}</h2>
          <p>{t('panel.coldStart')}</p>
          <Link to="/sources">{t('panel.coldStartCta')}</Link>
        </section>
      ) : null}

      {state.kind === 'ready' ? (
        <>
          <BridgeStrip items={state.data.bridge} onDrilldown={setDrilldown} />

          <div className="tabs" role="tablist" aria-label={t('panel.title')}>
            {(['comercial', 'digital'] as const).map((tab) => (
              <button
                key={tab}
                type="button"
                role="tab"
                id={`panel-tab-${tab}`}
                aria-selected={activeTab === tab}
                aria-controls={`panel-tabpanel-${tab}`}
                className={activeTab === tab ? 'tab tab-active' : 'tab'}
                onClick={() => setActiveTab(tab)}
              >
                {t(tab === 'comercial' ? 'panel.tabComercial' : 'panel.tabDigital')}
              </button>
            ))}
          </div>

          <div
            role="tabpanel"
            id="panel-tabpanel-comercial"
            aria-labelledby="panel-tab-comercial"
            className="tab-panel"
            hidden={activeTab !== 'comercial'}
          >
            {state.data.no_sales_data === true &&
            state.data.comercial.resultado.length === 0 &&
            state.data.comercial.preco.length === 0 &&
            state.data.comercial.praca.length === 0 ? (
              <section className="empty-state" aria-live="polite">
                <p>{t('panel.emptyComercial')}</p>
              </section>
            ) : (
              <>
                <PanelSection
                  title={t('panel.secaoResultado')}
                  items={state.data.comercial.resultado}
                  onDrilldown={setDrilldown}
                />
                <PanelSection
                  title={t('panel.secaoPreco')}
                  items={state.data.comercial.preco}
                  onDrilldown={setDrilldown}
                />
                <PanelSection
                  title={t('panel.secaoPraca')}
                  items={state.data.comercial.praca}
                  onDrilldown={setDrilldown}
                />
                {state.data.gaps.produto ? (
                  <ProdutoEmBreve />
                ) : (
                  <PanelSection
                    title={t('panel.secaoProduto')}
                    items={state.data.comercial.produto}
                    onDrilldown={setDrilldown}
                  />
                )}
              </>
            )}
          </div>

          <div
            role="tabpanel"
            id="panel-tabpanel-digital"
            aria-labelledby="panel-tab-digital"
            className="tab-panel"
            hidden={activeTab !== 'digital'}
          >
            {state.data.digital.midia_paga.length === 0 &&
            state.data.digital.organico.length === 0 ? (
              <section className="empty-state" aria-live="polite">
                <p>{t('panel.emptyDigital')}</p>
              </section>
            ) : (
              <>
                <PanelSection
                  title={t('panel.secaoMidiaPaga')}
                  items={state.data.digital.midia_paga}
                  onDrilldown={setDrilldown}
                />
                <PanelSection
                  title={t('panel.secaoOrganico')}
                  items={state.data.digital.organico}
                  onDrilldown={setDrilldown}
                />
              </>
            )}
          </div>
        </>
      ) : null}

      {drilldown !== null ? (
        <DrilldownDialog kpi={drilldown} onClose={() => setDrilldown(null)} />
      ) : null}
    </section>
  );
}

// ---------------------------------------------------------------------------
// DashboardFourPsPage — EXPORT PRINCIPAL
// Tela "Painel" fiel ao prototipo M3 Expressive.
// ---------------------------------------------------------------------------

export function DashboardFourPsPage(): JSX.Element {
  const navigate = useNavigate();
  // Filtros lidos do contexto global (escritos pelo topbar do AppLayout)
  const { applied } = useFilterContext();
  const { periodMode, appliedYear, appliedMonth, source } = applied;

  const [layer, setLayer] = useState<FunnelLayer>('visibilidade');
  const [state, setState] = useState<LoadState<FunnelData>>({ kind: 'loading' });
  const [drilldown, setDrilldown] = useState<PainelDrilldownItem | null>(null);
  const [retryKey, setRetryKey] = useState(0);

  useEffect(() => {
    let cancelled = false;
    setState({ kind: 'loading' });
    const url = buildFunnelUrl(periodMode, appliedYear, appliedMonth, source);
    const load = async (): Promise<void> => {
      try {
        const data = await apiClient.get<FunnelData | null>(url);
        if (cancelled) return;
        if (data === null) {
          setState({ kind: 'empty' });
          return;
        }
        if (isColdStartFunnel(data)) {
          setState({ kind: 'empty' });
          return;
        }
        setState({ kind: 'ready', data });
      } catch (err) {
        if (cancelled) return;
        if (err instanceof ApiError && err.status === 404) {
          setState({ kind: 'empty' });
          return;
        }
        setState({ kind: 'error' });
      }
    };
    void load();
    return () => {
      cancelled = true;
    };
  }, [periodMode, appliedYear, appliedMonth, source, retryKey]);

  const activeKpis: readonly FunnelKpi[] = state.kind === 'ready' ? state.data[layer] : [];

  const periodLabel = periodDisplayLabel(periodMode, appliedMonth, appliedYear);

  const heroData = state.kind === 'ready' ? state.data.hero : undefined;
  const headline = state.kind === 'ready' ? state.data.headline : undefined;
  const lastCollectedAt = state.kind === 'ready' ? state.data.last_collected_at : undefined;

  // Detecta se CPC subiu para badge de alerta na aba Trafego
  const trafegoAlerta =
    state.kind === 'ready' &&
    state.data.trafego.some(
      (k) =>
        k.title.toLowerCase().includes('cpc') &&
        k.delta !== undefined &&
        k.delta !== null &&
        k.delta > 0,
    );

  const layerDescKeys: Record<FunnelLayer, string> = {
    visibilidade: t('fourPs.layerDesc.visibilidade'),
    engajamento: t('fourPs.layerDesc.engajamento'),
    trafego: t('fourPs.layerDesc.trafego'),
    vendas: t('fourPs.layerDesc.vendas'),
  };

  const handleChatClick = (): void => {
    void navigate('/chat');
  };

  return (
    <>
      <section aria-labelledby="funnel-title">
        {/* Page Header Editorial — Space Grotesk 44px conforme prototipo */}
        <div className="painel-page-header">
          <div>
            <div className="painel-page-eyebrow">{PAGE_HEADER_STRINGS.eyebrow}</div>
            <h1 className="painel-page-title" id="funnel-title">
              {PAGE_HEADER_STRINGS.titlePre}
              <br />
              <em>{PAGE_HEADER_STRINGS.titleEm}</em>
            </h1>
            <div className="painel-page-sub">{PAGE_HEADER_STRINGS.sub}</div>
          </div>
          <div className="painel-page-meta">
            {state.kind === 'ready' ? (
              <>
                {PAGE_HEADER_STRINGS.metaPeriodLabel} <strong>{periodLabel}</strong>
                <br />
                {PAGE_HEADER_STRINGS.metaSources}
              </>
            ) : (
              <span className="painel-meta-loading">{PAGE_HEADER_STRINGS.metaLoading}</span>
            )}
          </div>
        </div>

        {/* Bento Hero — sempre renderizado, estados de dado exibem vazio honesto */}
        {state.kind !== 'loading' ? (
          <PainelHeroBento
            hero={heroData}
            headline={headline}
            periodLabel={periodLabel}
            lastCollectedAt={lastCollectedAt}
            onRevenueClick={() => {
              const item: PainelDrilldownItem = {
                id: 'hero-faturamento',
                label: 'Faturamento total',
                value: heroData?.faturamento ?? null,
                source: 'drive',
                sourceLabel: 'Planilhas do Drive',
                note: heroData?.faturamento_note ?? 'Dado da planilha de vendas no Google Drive.',
              };
              setDrilldown(item);
            }}
            onBolosClick={() => {
              const item: PainelDrilldownItem = {
                id: 'hero-bolos',
                label: 'Bolos vendidos',
                value: heroData?.bolos_vendidos ?? null,
                source: 'drive',
                sourceLabel: 'Planilhas do Drive',
                note: 'Quantidade total de itens vendidos no periodo.',
              };
              setDrilldown(item);
            }}
            onAlcanceClick={() => {
              const item: PainelDrilldownItem = {
                id: 'hero-alcance',
                label: 'Alcance total',
                value: heroData?.alcance_total ?? null,
                source: 'ig',
                sourceLabel: 'Instagram',
                note: 'Pessoas unicas que viram ao menos um post seu no Instagram.',
              };
              setDrilldown(item);
            }}
            onChatClick={handleChatClick}
          />
        ) : null}

        {/* Secao de Funil — Segmented Button + Grade KPI */}
        <div className="painel-funnel-section" aria-labelledby="funnel-section-title">
          <div className="painel-funnel-header">
            <div>
              {/* Kicker dinamico por camada — "FUNIL DO SEU NEGOCIO · VISIBILIDADE" */}
              <div className="painel-editorial-kicker">
                {HERO_STRINGS.section.toUpperCase()} · {LAYER_LABELS[layer].toUpperCase()}
              </div>
              <h2 id="funnel-section-title" className="painel-editorial-headline">
                {PAGE_HEADER_STRINGS.funnelHeadline}
              </h2>
              <p className="painel-funnel-desc" aria-live="polite" key={layer}>
                {layerDescKeys[layer]}
              </p>
            </div>

            <PainelSegmentedBtn
              activeLayer={layer}
              onChange={setLayer}
              alerts={{ trafego: trafegoAlerta }}
            />
          </div>

          {/* Tabpanel da camada ativa */}
          <div
            role="tabpanel"
            id={`painel-tabpanel-${layer}`}
            aria-labelledby={`painel-tab-${layer}`}
          >
            {state.kind === 'loading' ? <SkeletonGrid count={4} /> : null}

            {state.kind === 'error' ? (
              <div role="alert" className="error">
                <p>{t('fourPs.error')}</p>
                <button
                  type="button"
                  onClick={() => {
                    setRetryKey((k) => k + 1);
                  }}
                >
                  {t('fourPs.retryLabel')}
                </button>
              </div>
            ) : null}

            {state.kind === 'empty' ? (
              <section className="empty-state" aria-live="polite">
                <span className="empty-state-icon material-symbols-rounded" aria-hidden="true">
                  bar_chart
                </span>
                <h2>{t('fourPs.coldStartTitle')}</h2>
                <p>{t('fourPs.coldStart')}</p>
                <Link to="/sources">{t('fourPs.coldStartCta')}</Link>
              </section>
            ) : null}

            {state.kind === 'ready' && activeKpis.length === 0 ? (
              <section className="empty-state" aria-live="polite">
                <p>
                  {t(
                    `fourPs.emptyMonth${layer.charAt(0).toUpperCase()}${layer.slice(1)}` as
                      | 'fourPs.emptyMonthVisibilidade'
                      | 'fourPs.emptyMonthEngajamento'
                      | 'fourPs.emptyMonthTrafego'
                      | 'fourPs.emptyMonthVendas',
                  )}
                </p>
              </section>
            ) : null}

            {state.kind === 'ready' && activeKpis.length > 0 ? (
              <PainelKpiGrid kpis={activeKpis} onCardClick={setDrilldown} />
            ) : null}

            {/* Link contextual "Ver campanhas por separado" — aparece na aba Trafego
                quando a fonte ativa inclui Meta Ads (all ou meta_ads especificamente).
                Rota canonizada para /digital/campanhas (D-CAMP-01 atualizado). */}
            {state.kind === 'ready' &&
            layer === 'trafego' &&
            (source === 'all' || source === 'meta_ads') ? (
              <Link
                to="/digital/campanhas"
                className="ads-see-campaigns-link"
                aria-label={t('ads.viewCampaigns')}
              >
                <span
                  className="material-symbols-rounded"
                  style={{ fontSize: 14 }}
                  aria-hidden="true"
                >
                  campaign
                </span>
                {t('ads.viewCampaigns')}
                <span
                  className="material-symbols-rounded"
                  style={{ fontSize: 14 }}
                  aria-hidden="true"
                >
                  arrow_forward
                </span>
              </Link>
            ) : null}
          </div>
        </div>
      </section>

      {/* Modal de drilldown M3 */}
      <PainelDrilldownModal
        item={drilldown}
        onClose={() => setDrilldown(null)}
        strings={MODAL_STRINGS}
        onAsk={handleChatClick}
      />

      {/* FAB estendido — "Perguntar a CROSS" */}
      <button
        type="button"
        className="painel-fab"
        onClick={handleChatClick}
        aria-label={t('radar.fabLabel')}
      >
        <span className="painel-fab-icon material-symbols-rounded" aria-hidden="true">
          forum
        </span>
        <span className="painel-fab-label">{t('radar.fabLabel')}</span>
      </button>
    </>
  );
}

// ---------------------------------------------------------------------------
// Exportacao do novo Painel de duas camadas (legado preservado)
// ---------------------------------------------------------------------------

export { PanelView as DashboardPanelPage };
